import { Button } from "@/components/ui/button";
import { CheckCircle2, Calendar, Clock, MapPin, Video } from "lucide-react";
import { format, parse } from "date-fns";
import { fr } from "date-fns/locale";
import type { BookingData } from "../BookingModal";
import { generateGoogleCalendarUrl, downloadICSFile } from "@/utils/calendarExport";
import { BRAND } from "@/config/brand";

// Google Calendar icon - official Google Calendar logo
const GoogleCalendarIcon = () => (
  <img 
    src="https://www.gstatic.com/images/branding/product/1x/calendar_2020q4_48dp.png" 
    alt="Google Calendar" 
    className="w-5 h-5"
  />
);

// Apple icon - Apple logo
const AppleIcon = () => (
  <svg viewBox="0 0 384 512" className="w-4 h-5" fill="currentColor">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
  </svg>
);
interface BookingConfirmationProps {
  bookingData: BookingData;
  service: { title: string };
  isOnline: boolean;
  appointmentId: string | null;
  onClose: () => void;
  eventDetails?: {
    date: string;
    time: string;
    format: string;
    duration: string;
  };
  childFirstName?: string;
  practitionerPhone?: string;
}

export function BookingConfirmation({
  bookingData,
  service,
  isOnline,
  onClose,
  eventDetails,
  childFirstName,
  practitionerPhone,
}: BookingConfirmationProps) {
  // For events, use event details; for regular appointments, use booking data
  const displayDate = eventDetails?.date ? new Date(eventDetails.date) : bookingData.date;
  const displayTime = eventDetails?.time || bookingData.time;

  if (!displayDate || !displayTime) return null;

  // Create start and end dates for calendar
  const getCalendarDates = () => {
    const [hours, minutes] = displayTime.split(":").map(Number);
    const startDate = new Date(displayDate);
    startDate.setHours(hours, minutes, 0, 0);

    // Parse duration (e.g., "2h", "1h30", "90 min")
    let durationMinutes = 60; // default 1 hour
    if (eventDetails?.duration) {
      const durationStr = eventDetails.duration.toLowerCase();
      const hoursMatch = durationStr.match(/(\d+)\s*h/);
      const minsMatch = durationStr.match(/(\d+)\s*min/);
      if (hoursMatch) durationMinutes = parseInt(hoursMatch[1]) * 60;
      if (minsMatch) durationMinutes += parseInt(minsMatch[1]);
    }

    const endDate = new Date(startDate);
    endDate.setMinutes(endDate.getMinutes() + durationMinutes);

    return { startDate, endDate };
  };

  const getLocation = () => {
    if (eventDetails) {
      return eventDetails.format;
    }
    if (isOnline) {
      return "Consultation en ligne - Lien Google Meet envoyé par email";
    }
    return `Cabinet ${BRAND.name.full} - Adresse envoyée par email`;
  };

  const getDescription = () => {
    const consultantName = childFirstName 
      ? `${bookingData.firstName} ${bookingData.lastName} (pour ${childFirstName})`
      : `${bookingData.firstName} ${bookingData.lastName}`;
    
    const lines = [
      `Rendez-vous ${service.title}`,
      `Consultant: ${consultantName}`,
      "",
      `${BRAND.name.full} – ${BRAND.baseline.full}`,
    ];
    
    // Add practitioner phone if available
    if (practitionerPhone) {
      lines.push(`Téléphone: ${practitionerPhone}`);
    }
    
    lines.push(`Contact: contact@regard-de-nature.fr`);
    
    return lines.join("\n");
  };

  const getCalendarTitle = () => {
    if (childFirstName) {
      return `${service.title} (${childFirstName}) - ${BRAND.name.full}`;
    }
    return `${service.title} - ${BRAND.name.full}`;
  };

  const handleAddToGoogleCalendar = () => {
    const { startDate, endDate } = getCalendarDates();
    const url = generateGoogleCalendarUrl({
      title: getCalendarTitle(),
      description: getDescription(),
      location: getLocation(),
      startDate,
      endDate,
    });
    window.open(url, "_blank");
  };

  const handleDownloadICS = () => {
    const { startDate, endDate } = getCalendarDates();
    downloadICSFile({
      title: getCalendarTitle(),
      description: getDescription(),
      location: getLocation(),
      startDate,
      endDate,
    });
  };

  return (
    <div className="space-y-6 py-8 max-w-2xl mx-auto text-center">
      <div className="flex justify-center">
        <div className="bg-green-100 rounded-full p-4">
          <CheckCircle2 className="w-12 h-12 text-green-600" />
        </div>
      </div>

      <div className="space-y-2">
        <h2 className="text-3xl font-bold text-primary">Rendez-vous confirmé !</h2>
        <p className="text-muted-foreground">
          Un email de confirmation a été envoyé à <strong>{bookingData.email}</strong>
        </p>
      </div>

      <div className="bg-gradient-card rounded-lg p-6 space-y-4 text-left">
        <h3 className="font-semibold text-xl text-primary">{service.title}</h3>

        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-secondary mt-0.5" />
            <div>
              <p className="font-medium">{format(displayDate, "EEEE d MMMM yyyy", { locale: fr })}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-secondary mt-0.5" />
            <div>
              <p className="font-medium">{displayTime}</p>
              <p className="text-sm text-muted-foreground">Heure d'Europe centrale</p>
            </div>
          </div>

          {eventDetails ? (
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-secondary mt-0.5" />
              <div>
                <p className="font-medium">{eventDetails.format}</p>
                <p className="text-sm text-muted-foreground">Durée : {eventDetails.duration}</p>
              </div>
            </div>
          ) : isOnline ? (
            <div className="flex items-start gap-3">
              <Video className="w-5 h-5 text-secondary mt-0.5" />
              <div>
                <p className="font-medium">Consultation en ligne</p>
                <p className="text-sm text-muted-foreground">
                  Un lien de visioconférence Google Meet vous a été envoyé par email
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-secondary mt-0.5" />
              <div>
                <p className="font-medium">Consultation au cabinet</p>
                {bookingData.locationName ? (
                  <p className="text-sm text-muted-foreground">
                    {bookingData.locationName}{bookingData.locationCity ? ` · ${bookingData.locationCity}` : ""}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">L'adresse du cabinet vous a été envoyée par email</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="pt-4 border-t space-y-2">
          <p className="text-sm">
            <strong>Patient :</strong> {bookingData.firstName} {bookingData.lastName}
          </p>
          {bookingData.phone && (
            <p className="text-sm">
              <strong>Téléphone :</strong> {bookingData.phone}
            </p>
          )}
        </div>
      </div>

      {/* Calendar buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button variant="outline" onClick={handleAddToGoogleCalendar} className="flex items-center gap-2">
          <GoogleCalendarIcon />
          Ajouter à Google Calendar
        </Button>
        <Button variant="outline" onClick={handleDownloadICS} className="flex items-center gap-2">
          <AppleIcon />
          Ajouter au Calendrier (Apple/iOS)
        </Button>
      </div>

      {!isOnline && !eventDetails && (bookingData.locationCity?.toLowerCase().includes("montesquieu") || bookingData.locationName?.toLowerCase().includes("montesquieu")) && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
          <p className="text-sm text-amber-900">
            <strong>À noter :</strong> Le cabinet de Montesquieu-Lauragais ne dispose pas de salle d'attente. Merci de respecter l'horaire indiqué pour votre rendez-vous.
          </p>
        </div>
      )}

      <Button onClick={onClose} className="w-full max-w-xs">
        Fermer
      </Button>
    </div>
  );
}
